import { ErrorModal, HelmetDexteel, useUserContext } from "@dexteel/mesf-core";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import {
	Button,
	Grid2,
	IconButton,
	Paper,
	Tooltip,
	Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import type {
	ColDef,
	GridApi,
	ICellRendererParams,
	RowDoubleClickedEvent,
	SelectionChangedEvent,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import { useCallback, useMemo, useState } from "react";

import { useGridDefinitions } from "@/controls/ag-grid/components/GridDefinitions";
import { CenteredLazyLoading } from "@/controls/LazyLoading";
import {
	PROFILE_ITEM_MODAL_MODE,
	type ProfileItem,
	type ProfileItemModalMode,
} from "../../models/ProfileItem";
import { getProfileItems } from "../../repositories/ProfileItemRepository";
import { BulkDeleteModal } from "./components/BulkDeleteModal";
import { UpsertDeleteModal } from "./components/UpsertDeleteModal";

interface DimensionActionsProps {
	dimension: ProfileItem;
	onEdit: (dimension: ProfileItem) => void;
	onDelete: (dimension: ProfileItem) => void;
}

const DimensionActions = ({
	dimension,
	onEdit,
	onDelete,
}: DimensionActionsProps) => (
	<Grid2
		container
		alignItems="center"
		justifyContent="center"
		className="dimension-actions"
		sx={{ height: "100%", gap: 1 }}
	>
		<Tooltip title="Edit">
			<IconButton
				data-test-id="edit-dimension-button"
				aria-label={`Edit ${dimension.Code}`}
				onClick={() => onEdit(dimension)}
				size="small"
			>
				<EditIcon color="primary" />
			</IconButton>
		</Tooltip>
		<Tooltip title="Delete">
			<IconButton
				data-test-id="delete-dimension-button"
				aria-label={`Delete ${dimension.Code}`}
				onClick={() => onDelete(dimension)}
				size="small"
			>
				<DeleteIcon color="secondary" />
			</IconButton>
		</Tooltip>
	</Grid2>
);

export const SectionDimensionsPage = () => {
	const {
		state: { defaultAreaId },
	} = useUserContext();
	const [gridApi, setGridApi] = useState<GridApi<ProfileItem> | null>(null);
	const [modal, setModal] = useState<ProfileItemModalMode>(
		PROFILE_ITEM_MODAL_MODE.CLOSED,
	);
	const [selectedDimension, setSelectedDimension] =
		useState<ProfileItem | null>(null);
	const [selectedDimensions, setSelectedDimensions] = useState<ProfileItem[]>(
		[],
	);
	const [showBulkDelete, setShowBulkDelete] = useState(false);
	const [queryError, setQueryError] = useState("");

	const { data: rows, isLoading } = useQuery<ProfileItem[], Error>({
		queryKey: ["sectionDimensions", defaultAreaId],
		queryFn: ({ signal }) => getProfileItems(defaultAreaId, signal),
		onError: (error) => setQueryError(error.message),
	});

	const openModal = useCallback(
		(mode: ProfileItemModalMode, dimension: ProfileItem | null) => {
			setSelectedDimension(dimension);
			setModal(mode);
		},
		[],
	);

	const handleEdit = useCallback(
		(dimension: ProfileItem) =>
			openModal(PROFILE_ITEM_MODAL_MODE.UPDATE, dimension),
		[openModal],
	);

	const handleDelete = useCallback(
		(dimension: ProfileItem) =>
			openModal(PROFILE_ITEM_MODAL_MODE.DELETE, dimension),
		[openModal],
	);

	const columnDefs = useMemo<ColDef<ProfileItem>[]>(
		() => [
			{
				field: "Code",
				headerName: "Name",
				minWidth: 90,
				flex: 0.4,
			},
			{
				field: "Name",
				headerName: "Description",
				minWidth: 280,
				flex: 2,
			},
			{
				field: "ReferenceCount",
				headerName: "Section Specs",
				minWidth: 150,
				maxWidth: 200,
				flex: 1,
			},
			{
				headerName: "",
				cellRenderer: (params: ICellRendererParams<ProfileItem>) =>
					params.data ? (
						<DimensionActions
							dimension={params.data}
							onEdit={handleEdit}
							onDelete={handleDelete}
						/>
					) : null,
				minWidth: 120,
				maxWidth: 140,
				sortable: false,
				filter: false,
			},
		],
		[handleDelete, handleEdit],
	);

	const { defaultColDef } = useGridDefinitions({});

	const handleRowDoubleClicked = (
		event: RowDoubleClickedEvent<ProfileItem>,
	) => {
		if (event.data) handleEdit(event.data);
	};

	const handleSelectionChanged = (
		event: SelectionChangedEvent<ProfileItem>,
	) => {
		setSelectedDimensions(event.api.getSelectedRows());
	};

	const handleModalHide = (shouldUpdate: boolean) => {
		if (shouldUpdate) gridApi?.paginationGoToLastPage();
		setModal(PROFILE_ITEM_MODAL_MODE.CLOSED);
		setSelectedDimension(null);
	};

	const handleNewDimension = () => {
		gridApi?.deselectAll();
		setSelectedDimensions([]);
		openModal(PROFILE_ITEM_MODAL_MODE.CREATE, null);
	};

	const handleBulkDeleteHide = (deleted: boolean) => {
		if (deleted) {
			gridApi?.deselectAll();
			setSelectedDimensions([]);
		}
		setShowBulkDelete(false);
	};

	return (
		<>
			<HelmetDexteel title="Section Dimensions" />
			<Grid2 container justifyContent="flex-start" p={1} spacing={1}>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<Typography variant="h5" fontWeight={600}>
						Section Dimensions
					</Typography>
				</Grid2>
				<Grid2 size={{ md: 12, xs: 12 }}>
					<Paper
						data-test-id="section-dimensions-grid"
						elevation={2}
						sx={{
							height: "64vh",
							width: "100%",
							"& .dimension-actions": {
								opacity: 0,
								transition: "opacity 0.1s ease-in-out",
							},
							"& .ag-row-hover .dimension-actions": { opacity: 1 },
						}}
					>
						<AgGridReact<ProfileItem>
							loading={isLoading}
							rowData={rows ?? []}
							columnDefs={columnDefs}
							defaultColDef={defaultColDef}
							rowHeight={34}
							headerHeight={34}
							animateRows
							onRowDoubleClicked={handleRowDoubleClicked}
							onGridReady={(params) => setGridApi(params.api)}
							onSelectionChanged={handleSelectionChanged}
							getRowId={(params) => String(params.data.ProfileItemId)}
							loadingOverlayComponent={CenteredLazyLoading}
							gridOptions={{
								rowSelection: {
									mode: "multiRow",
									checkboxes: true,
									headerCheckbox: false,
									enableClickSelection: false,
								},
							}}
						/>
					</Paper>
				</Grid2>
				<Grid2
					container
					size={{ md: 12, xs: 12 }}
					justifyContent="flex-end"
					spacing={1}
				>
					{selectedDimensions.length > 1 && (
						<Grid2 size={{ md: 3, xs: 12 }}>
							<Button
								variant="contained"
								color="secondary"
								fullWidth
								onClick={() => setShowBulkDelete(true)}
							>
								DELETE
							</Button>
						</Grid2>
					)}
					<Grid2 size={{ md: 3, xs: 12 }}>
						<Button
							variant="contained"
							color="primary"
							fullWidth
							data-test-id="new-dimension-button"
							onClick={handleNewDimension}
						>
							NEW SECTION DIMENSION
						</Button>
					</Grid2>
				</Grid2>
			</Grid2>
			<UpsertDeleteModal
				dimension={selectedDimension}
				modal={modal}
				onHide={handleModalHide}
			/>
			<BulkDeleteModal
				dimensions={selectedDimensions}
				open={showBulkDelete}
				onHide={handleBulkDeleteHide}
			/>
			<ErrorModal error={queryError} onHide={() => setQueryError("")} />
		</>
	);
};
